import type { IBusiness, OnboardingStep } from "@/business/business";
import { isProductEnabled } from "@/business/product-access";
import { isPayrollBusiness } from "@/core/business/payroll-only";
import { PAY_BREAKDOWN_PATH } from "@/payroll/pay-breakdown/routes";
import { isAdminOrOwnerOrManager } from "@/user/utils";
import { ONBOARDING_STEP } from "./constants";
import type { BusinessOnboarding, IntakeStep } from "./simple-onboarding/types";

export const SIMPLE_ONBOARDING_PATHS = {
  root: "/onboarding",
  welcome: "/onboarding/welcome",
  company: "/onboarding/company",
  schedule: "/onboarding/schedule",
  referenceData: "/onboarding/reference-data",
  payPlan: "/onboarding/pay-plan",
  submitted: "/onboarding/submitted",
  invoiceSources: "/onboarding/invoice-sources",
  forwardingSetup: "/onboarding/forwarding-setup",
  invoiceUpload: "/onboarding/invoice-upload",
  stAccess: "/onboarding/servicetitan-access",
  onboardingCall: "/onboarding/call",
  forwardingGuides: "/onboarding/forwarding-guides",
  // Kept so an old link or bookmark still lands somewhere sensible.
  setup: "/onboarding/setup",
  documents: "/onboarding/documents",
} as const;

export const ONBOARDING_PANE = {
  welcome: "welcome",
  company: "company",
  schedule: "schedule",
  payPlan: "pay-plan",
  submitted: "submitted",
  referenceData: "reference-data",
  invoiceSources: "invoice-sources",
  forwardingSetup: "forwarding-setup",
  invoiceUpload: "invoice-upload",
  stAccess: "servicetitan-access",
  onboardingCall: "call",
  forwardingGuides: "forwarding-guides",
} as const;

export type OnboardingPane =
  (typeof ONBOARDING_PANE)[keyof typeof ONBOARDING_PANE];

export type OnboardingStepPane = Exclude<
  OnboardingPane,
  typeof ONBOARDING_PANE.welcome | typeof ONBOARDING_PANE.submitted
>;

// The steps the progress bar shows. Welcome is the introduction rather than a step, so it
// is not in here: nothing is asked of them on it.
export const ONBOARDING_STEPS: {
  pane: OnboardingStepPane;
  path: string;
  label: string;
}[] = [
  {
    pane: ONBOARDING_PANE.company,
    path: SIMPLE_ONBOARDING_PATHS.company,
    label: "Company",
  },
  {
    pane: ONBOARDING_PANE.schedule,
    path: SIMPLE_ONBOARDING_PATHS.schedule,
    label: "Pay schedule",
  },
  {
    pane: ONBOARDING_PANE.referenceData,
    path: SIMPLE_ONBOARDING_PATHS.referenceData,
    label: "Reference data",
  },
  {
    pane: ONBOARDING_PANE.payPlan,
    path: SIMPLE_ONBOARDING_PATHS.payPlan,
    label: "Pay plan",
  },
];

// The pane a screen renders under names the task it is, which the aside needs to mark
// the current row while a jump is still in flight.
export const PANE_INTAKE_STEPS: Partial<Record<OnboardingPane, IntakeStep>> = {
  [ONBOARDING_PANE.company]: "company",
  [ONBOARDING_PANE.schedule]: "schedule",
  [ONBOARDING_PANE.referenceData]: "reference_data",
  [ONBOARDING_PANE.payPlan]: "pay_plan",
  [ONBOARDING_PANE.invoiceSources]: "invoice_sources",
  [ONBOARDING_PANE.forwardingSetup]: "forwarding_setup",
  [ONBOARDING_PANE.invoiceUpload]: "invoice_upload",
  [ONBOARDING_PANE.stAccess]: "st_access",
  [ONBOARDING_PANE.onboardingCall]: "onboarding_call",
};

export const onboardingStepIndex = (pane: OnboardingStepPane | undefined) =>
  ONBOARDING_STEPS.findIndex((step) => step.pane === pane);

export const isSimpleOnboardingRequired = (
  onboardingStep: OnboardingStep | null | undefined,
) => Boolean(onboardingStep) && onboardingStep !== ONBOARDING_STEP.completed;

export const INTAKE_STEP_PATHS: Record<IntakeStep, string> = {
  welcome: SIMPLE_ONBOARDING_PATHS.welcome,
  company: SIMPLE_ONBOARDING_PATHS.company,
  schedule: SIMPLE_ONBOARDING_PATHS.schedule,
  reference_data: SIMPLE_ONBOARDING_PATHS.referenceData,
  pay_plan: SIMPLE_ONBOARDING_PATHS.payPlan,
  invoice_sources: SIMPLE_ONBOARDING_PATHS.invoiceSources,
  forwarding_setup: SIMPLE_ONBOARDING_PATHS.forwardingSetup,
  invoice_upload: SIMPLE_ONBOARDING_PATHS.invoiceUpload,
  st_access: SIMPLE_ONBOARDING_PATHS.stAccess,
  onboarding_call: SIMPLE_ONBOARDING_PATHS.onboardingCall,
  waiting: SIMPLE_ONBOARDING_PATHS.submitted,
};

export const INTAKE_STEP_LABELS: Record<IntakeStep, string> = {
  welcome: "Welcome",
  company: "Company",
  schedule: "Pay schedule",
  reference_data: "Reference data",
  pay_plan: "Pay plan",
  invoice_sources: "Invoice sources",
  forwarding_setup: "Send invoices to Volca",
  invoice_upload: "Sample invoices",
  st_access: "ServiceTitan access",
  onboarding_call: "Review and submit",
  waiting: "Waiting for setup",
};

// The API owns rollout migration and resume position. Filled fields cannot tell
// us whether a customer deliberately went back to an earlier screen.
export const resumeOnboardingPath = (
  state: Pick<BusinessOnboarding, "current_step">,
) => INTAKE_STEP_PATHS[state.current_step];

export const onboardingProductPath = (business: IBusiness) => {
  const settings = business.enabled_products;
  if (isProductEnabled(settings, "payroll") && isPayrollBusiness(business)) {
    return PAY_BREAKDOWN_PATH;
  }
  if (isProductEnabled(settings, "accounts_payable"))
    return "/accounts-payable";
  // Legacy completed businesses keep their normal landing route and permissions.
  return "/";
};

export const requiresSimpleOnboardingRedirect = (
  onboardingStep: OnboardingStep | null | undefined,
  pathname: string,
) =>
  isSimpleOnboardingRequired(onboardingStep) &&
  !pathname.startsWith(SIMPLE_ONBOARDING_PATHS.root);

/**
 * Whether this user may run simple onboarding, rather than wait for someone who can.
 *
 * Mirrors `User#may_manage_business_setup?` on the API, which is what actually enforces
 * it — this only decides which screen to show. A technician or rep at a setting-up
 * business gets the waiting screen: they cannot rename the company, change its pay
 * schedule, or read its payroll documents.
 */
export const canManageSimpleOnboarding = (role: string | undefined) =>
  isAdminOrOwnerOrManager(role);

export const hasOnboardingProducts = (settings: unknown) =>
  settings == null ||
  isProductEnabled(settings, "payroll") ||
  isProductEnabled(settings, "accounts_payable");
